import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  InternalIngestionFolderFragment,
  TxPipelineNeedsInvestigationReason,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC, ReactNode, useMemo } from 'react';
import { match } from 'ts-pattern';
import { getInvestigationReasonLabel } from '../utils/transactionPipeline.utils';

dayjs.extend(relativeTime);

type NullableFolder = InternalIngestionFolderFragment | null | undefined;

export const PipelineStageSelectionTooltipContent: FC = () => (
  <Box maxW="400px">
    <Text fontWeight="bold" mb={2}>
      Pipeline & Stage Selection Logic
    </Text>

    <Text fontSize="sm" fontWeight="bold" mb={1}>
      1. Target State Identification:
    </Text>
    <Text fontSize="xs" mb={2} pl={2}>
      • First: Document versions (most common state among classified docs)
      <br />
      • Fallback: Property records → MLS listings → Normalized contract fields
      <br />• Must find valid US state to proceed
    </Text>

    <Text fontSize="sm" fontWeight="bold" mb={1}>
      2. Pipeline Matching:
    </Text>
    <Text fontSize="xs" mb={2} pl={2}>
      Matches pipeline by 3 criteria:
      <br />• <strong>Client Type:</strong> Derived from folder transaction side
      (BUY→BUYER, SELL/BUY_SELL→SELLER)
      <br />• <strong>Property Type:</strong> From HouseCanary records or MLS
      (mapped to RESIDENTIAL/LAND/COMMERCIAL/RENTAL)
      <br />• <strong>US State:</strong> From step 1
      <br />
      <br />
      If no direct match: Falls back to evaluating pipeline rules against folder
      autofill data
    </Text>

    <Text fontSize="sm" fontWeight="bold" mb={1}>
      3. Stage Selection:
    </Text>
    <Text fontSize="xs" pl={2}>
      • Evaluates all stages in order (by sortOrder)
      <br />
      • Each stage has requirement rules that ALL must pass
      <br />
      • Rules check: autofill values, document existence, field values
      <br />• Selects LAST enabled stage (highest sortOrder where requirements
      pass)
    </Text>
  </Box>
);

interface PipelineStageSelectionInfoProps {
  folder?: NullableFolder;
}

const debugTextProps = {
  color: 'compliance.debug.text',
  fontSize: 'xs',
} as const;

const DebugText: FC<{ children: ReactNode; opacity?: number }> = ({
  children,
  opacity,
}) => (
  <Text {...debugTextProps} opacity={opacity}>
    {children}
  </Text>
);

const SectionHeader: FC<{ children: ReactNode }> = ({ children }) => (
  <Text
    color="compliance.debug.text"
    fontSize="xs"
    fontWeight="bold"
    letterSpacing="wide"
    opacity={0.9}
    textTransform="uppercase"
  >
    {children}
  </Text>
);

const KeyValueRow: FC<{ label: string; value: ReactNode }> = ({
  label,
  value,
}) => (
  <HStack align="start" justify="space-between" spacing={3}>
    <DebugText opacity={0.7}>{label}:</DebugText>
    <Box maxW="60%" textAlign="right">
      {typeof value === 'string' ? <DebugText>{value}</DebugText> : value}
    </Box>
  </HStack>
);

const getStringValue = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

/**
 * Returns actionable guidance text for a given pipeline investigation reason.
 * @param reason - The investigation reason indicating why pipeline selection needs attention
 * @returns User-facing guidance text suggesting how to resolve the issue
 */
const getPipelineFixSuggestion = (
  reason: TxPipelineNeedsInvestigationReason
): string =>
  match(reason)
    .with(
      TxPipelineNeedsInvestigationReason.NO_TARGET_STATE,
      () => 'Add a valid property state (MLS address or normalized fields).'
    )
    .with(
      TxPipelineNeedsInvestigationReason.NO_PIPELINES_FOR_TARGET_STATE,
      () =>
        'Create pipeline rules for the detected state or adjust matching rules.'
    )
    .with(
      TxPipelineNeedsInvestigationReason.MULTIPLE_CANDIDATES,
      () => 'Tighten pipeline rules or metadata so a single candidate matches.'
    )
    .with(
      TxPipelineNeedsInvestigationReason.NOT_IN_CANDIDATES,
      () => 'Re-run matching or align folder metadata with pipeline rules.'
    )
    .with(
      TxPipelineNeedsInvestigationReason.HAS_PIPELINE_ZERO_CANDIDATES,
      () =>
        'Refresh pipeline sync; ensure current pipeline still satisfies rules.'
    )
    .with(
      TxPipelineNeedsInvestigationReason.NO_PIPELINE,
      () =>
        'No pipeline matched; confirm state, property type, and client side are present.'
    )
    .with(TxPipelineNeedsInvestigationReason.NONE, () => 'No action needed.')
    .otherwise(() => 'No action needed.');

export const PipelineStageSelectionInfo: FC<
  PipelineStageSelectionInfoProps
> = ({ folder }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const pipeline = folder?.transactionPipeline;

  const metadata = useMemo(
    () =>
      folder?.metadata ? (folder.metadata as Record<string, unknown>) : {},
    [folder?.metadata]
  );

  const normalizedValues = useMemo(
    () =>
      folder?.normalizedFieldValues
        ? (folder.normalizedFieldValues as Record<string, unknown>)
        : {},
    [folder?.normalizedFieldValues]
  );

  const normalizedAddress =
    getStringValue(normalizedValues.property_full_address) ||
    getStringValue(metadata.property_full_address);

  const mlsFullAddress = useMemo(() => {
    const addr = folder?.mlsListing?.address;
    if (!addr?.full) {
      return 'MLS address not available';
    }

    const parts = [
      addr.full,
      addr.unit,
      addr.city,
      addr.state,
      addr.postalCode,
    ].filter(Boolean);

    return parts.join(', ');
  }, [folder?.mlsListing?.address]);

  const pipelineCandidates = folder?.txPipelineCandidates ?? [];
  const needsInvestigationReason =
    folder?.txPipelineNeedsInvestigationReason ??
    TxPipelineNeedsInvestigationReason.NONE;

  const candidateBadgeColor =
    pipelineCandidates.length === 1 &&
    needsInvestigationReason === TxPipelineNeedsInvestigationReason.NONE
      ? 'green'
      : 'red';

  const investigationReasonLabel = getInvestigationReasonLabel(
    needsInvestigationReason
  );

  const currentPipelineUuid = folder?.transactionPipeline?.id;
  const currentPipelineCandidate = pipelineCandidates.find(
    (candidate) => candidate.pipelineUuid === currentPipelineUuid
  );
  const pipelineDisplayName =
    currentPipelineCandidate?.pipelineName || pipeline?.id;

  const stageNotSetReason = pipeline
    ? 'No stage matched current rules yet.'
    : 'Stage cannot be selected until a pipeline is set.';

  const stageFix = pipeline
    ? 'Verify required documents/fields for stage rules and rerun validation.'
    : 'Provide state, property type, and client side so a pipeline can be matched.';

  if (!folder) {
    return <DebugText opacity={0.6}>Folder data unavailable</DebugText>;
  }

  return (
    <>
      <SimpleGrid columns={1} spacingY={1}>
        {/* Current Selection */}
        <SectionHeader>Current Pipeline & Stage</SectionHeader>
        <KeyValueRow
          label="Pipeline"
          value={
            <VStack align="flex-end" spacing={0.5}>
              <HStack spacing={2}>
                <Badge colorScheme={pipeline ? 'green' : 'red'}>
                  {pipeline ? 'Set' : 'Not set'}
                </Badge>
                <DebugText>
                  {pipelineDisplayName || 'No pipeline selected'}
                </DebugText>
              </HStack>
              <DebugText opacity={0.8}>
                {pipeline
                  ? `${pipeline.clientType || 'Unknown client'} • ${
                      pipeline.transactionPropertyType || 'Unknown property'
                    } • ${pipeline.usState || 'Unknown state'}`
                  : `${investigationReasonLabel} — ${getPipelineFixSuggestion(
                      needsInvestigationReason
                    )}`}
              </DebugText>
              {pipeline &&
                needsInvestigationReason !==
                  TxPipelineNeedsInvestigationReason.NONE && (
                  <DebugText opacity={0.7}>
                    Issue: {investigationReasonLabel}. Fix:{' '}
                    {getPipelineFixSuggestion(needsInvestigationReason)}
                  </DebugText>
                )}
            </VStack>
          }
        />
        <KeyValueRow
          label="Stage"
          value={
            <VStack align="flex-end" spacing={0.5}>
              <HStack spacing={2}>
                <Badge colorScheme={folder.stage ? 'green' : 'red'}>
                  {folder.stage ? 'Set' : 'Not set'}
                </Badge>
                <DebugText>{folder.stage || 'No stage selected'}</DebugText>
              </HStack>
              <DebugText opacity={0.8}>
                {folder.stage ? 'Matched stage within current pipeline.' : stageNotSetReason}
              </DebugText>
              {!folder.stage && <DebugText opacity={0.7}>Fix: {stageFix}</DebugText>}
            </VStack>
          }
        />

        <Divider borderColor="compliance.debug.text" my={2} opacity={0.3} />

        {/* Matching Section */}
        <SectionHeader>Matching</SectionHeader>
        <KeyValueRow
          label="Tx Pipeline Matched By"
          value={folder.txPipelineMatchedBy || 'not available'}
        />
        <KeyValueRow
          label="Tx Pipeline Updated At"
          value={
            folder.txPipelineUpdatedAt ? (
              <Tooltip
                label={new Date(
                  folder.txPipelineUpdatedAt as string
                ).toLocaleString()}
              >
                <Text
                  {...debugTextProps}
                  cursor="help"
                  textDecoration="underline"
                  textDecorationStyle="dotted"
                >
                  {dayjs(folder.txPipelineUpdatedAt as string).fromNow()}
                </Text>
              </Tooltip>
            ) : (
              'not available'
            )
          }
        />
        <KeyValueRow
          label="Pipeline candidates"
          value={
            <Button
              colorScheme={candidateBadgeColor}
              size="xs"
              variant="solid"
              onClick={onOpen}
            >
              {pipelineCandidates.length} candidate
              {pipelineCandidates.length === 1 ? '' : 's'}
            </Button>
          }
        />

        <Divider borderColor="compliance.debug.text" my={2} opacity={0.3} />

        {/* Results Section */}
        <SectionHeader>Signals</SectionHeader>
        <KeyValueRow label="MLS Address" value={mlsFullAddress} />
        <KeyValueRow
          label="MLS State"
          value={folder.mlsListing?.address?.state || 'not available'}
        />
        <KeyValueRow
          label="MLS Property Type"
          value={folder.mlsListing?.property?.type || 'not available'}
        />
        <KeyValueRow
          label="MLS Property Subtype"
          value={
            folder.mlsListing?.property?.subTypeText ||
            folder.mlsListing?.property?.subType ||
            'not available'
          }
        />
        <KeyValueRow
          label="MLS Listing ID"
          value={
            getStringValue(normalizedValues.mls_listing_id) ||
            folder.activeMlsListingId ||
            'not available'
          }
        />
        <KeyValueRow
          label="Normalized Address"
          value={normalizedAddress || 'not available'}
        />
        <KeyValueRow
          label="Normalized State"
          value={
            getStringValue(normalizedValues.property_state) || 'not available'
          }
        />
        <KeyValueRow
          label="Normalized Transaction Side"
          value={
            getStringValue(normalizedValues.transaction_side) || 'not available'
          }
        />
        <KeyValueRow
          label="Folder Transaction Side"
          value={folder.transactionSide || 'not set'}
        />
        <KeyValueRow
          label="Metadata Transaction Status"
          value={getStringValue(metadata.transaction_status) || 'not set'}
        />
        <KeyValueRow
          label="Normalized Status"
          value={folder.normalizedStatus || 'not set'}
        />
        <KeyValueRow
          label="Metadata Office"
          value={
            getStringValue(metadata.office_name) ||
            getStringValue(metadata.office_id) ||
            'not available'
          }
        />
      </SimpleGrid>

      <Modal isOpen={isOpen} size="5xl" onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Pipeline candidates</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text color="gray.600" fontSize="sm" mb={3}>
              {investigationReasonLabel}
            </Text>
            <Table size="sm" variant="striped">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Client Type</Th>
                  <Th>State</Th>
                  <Th>Property Type</Th>
                  <Th>Matched By</Th>
                  <Th>Rule Formula</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pipelineCandidates.map((candidate) => {
                  const isCurrent =
                    currentPipelineUuid === candidate.pipelineUuid;

                  return (
                    <Tr
                      key={candidate.pipelineUuid}
                      bg={isCurrent ? 'green.50' : undefined}
                    >
                      <Td fontWeight={isCurrent ? 'bold' : 'normal'}>
                        {candidate.pipelineName || candidate.pipelineUuid}
                      </Td>
                      <Td>{candidate.clientType || '-'}</Td>
                      <Td>{candidate.usState || '-'}</Td>
                      <Td>{candidate.transactionPropertyType || '-'}</Td>
                      <Td>
                        <HStack spacing={1} wrap="wrap">
                          {candidate.matchedBy.map((matched) => (
                            <Badge key={matched} colorScheme="blue">
                              {matched}
                            </Badge>
                          ))}
                        </HStack>
                      </Td>
                      <Td maxW="240px" wordBreak="break-word">
                        <Tooltip label={candidate.ruleMatchFormula || 'N/A'}>
                          <Text noOfLines={2}>
                            {candidate.ruleMatchFormula || 'N/A'}
                          </Text>
                        </Tooltip>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
